import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { submitNote } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { getPartiesMap, InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { noteInputs } from '../constant'
import { type INoteButton, type INoteTemplate, IButtonType } from './types'

export function NoteDetails(props: {
  defaults: INoteTemplate | null
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
}) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [links, setLinks] = useState<INoteButton[]>([])
  const [showError, setShowError] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState<string>('')
  const { defaults = null, lastUpdatedAt } = props

  const activeInputRef = useRef(null)

  useEffect(() => {
    if (!defaults) {
      setInputs(noteInputs())
      setLinks([])
      return
    }
    const newStats = cloneDeep(noteInputs()) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    setInputs(newStats)
    setLinks(defaults?.links || [])
  }, [defaults, lastUpdatedAt])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const handleFocus = (event: any, fieldKey: string) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField(fieldKey)
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input: any = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    onChange(focusField, newValue)
    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const addLink = () => {
    const temp = cloneDeep(links)
    temp.push({ label: '', url: '', type: 'button' })
    setLinks(temp)
  }

  const onChangeLink = (key: string, value: any, index: number) => {
    const temp = cloneDeep(links)
    ;(temp as any)[index][key] = value
    setLinks(temp)
  }

  const removeLink = (index: number) => {
    const temp = cloneDeep(links)
    if (temp.length) temp.splice(index, 1)
    setLinks(temp)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    links.map((item) => {
      if (!item.label || !item.url) hasError = true
    })
    data.links = links

    if (hasError) {
      setInputs(newStats)
      setShowError(true)
      return
    }

    setLoading(true)
    const id = defaults ? defaults.id : 0
    submitNote(id, data)
      .then(() => {
        if (id == 0) toast('New note is added.', { type: 'success' })
        else toast(`Note is updated.`, { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const placeholders = getPartiesMap({})

  const renderLinks = () => {
    return links?.map((link: Record<string, any>, index: number) => {
      const input: InputType = {
        inputType: 'text',
        title: ``,
        value: link.url,
        prefix: 'Link:',
        indent: 12,
        showLinkDir: true,
      }

      input.additionalElements = (
        <>
          <input
            className="border-gray-300 border-[1px] px-1"
            value={link.label}
            onChange={(e: any) => onChangeLink('label', e.target.value, index)}
            placeholder={`${(IButtonType as any)[link.type]} Label`}
          />
          <select
            name={`button-type-${index}`}
            id={`button-type-${index}`}
            className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
            value={link.type}
            onChange={(e) => onChangeLink('type', e.target.value, index)}
          >
            {Object.keys(IButtonType).map((key) => {
              return (
                <option value={key} key={key}>
                  {(IButtonType as any)[key]}
                </option>
              )
            })}
          </select>
          <span className="cursor-pointer text-red-800" onClick={() => removeLink(index)}>
            <TrashIcon className="w-4 h-4" />
          </span>
        </>
      )

      if (showError && !(link.label && link.url)) {
        input.error = `${link.label ? '' : 'Label is required.'} ${link.url ? '' : 'Link is required.'}`
      }

      return (
        <div className={`col-span-${input.span || 1} opacity-100`} key={index}>
          <RenderInput
            input={input}
            Key={`link-${index}`}
            key={`link-${index}`}
            onChange={(key: string, value: any) => onChangeLink('url', value, index)}
          />
        </div>
      )
    })
  }

  return (
    <div className="note-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Notes - {!defaults ? 'Add' : `Update`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Notes</p>
        </div>
      </Button>

      <div className="my-4">
        <div className="border-b mb-2">- Placeholders</div>
        <pre className="flex gap-1 flex-wrap">
          {Object.keys(placeholders)
            .sort()
            .map((key, index) => {
              return (
                <div
                  key={index}
                  className="mr-2 cursor-pointer hover:underline"
                  onClick={() => insertTextAtCursor(`{{${key}}`)}
                >
                  {key},{' '}
                </div>
              )
            })}
        </pre>
      </div>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          return (
            <div className={`input col-span-${input.span}`} key={index}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                onFocus={(e: any) => handleFocus(e, key)}
                onKeyUp={handleCursorChange}
                onClick={handleCursorChange}
              />
            </div>
          )
        })}
        {renderLinks()}
        <div className="col-span-full">
          <Button link onClick={addLink}>
            + Add Button & Link
          </Button>
        </div>
      </div>
      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'} Note</>
        </Button>
      </div>
    </div>
  )
}
